import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { ingredientsApi } from '../../api/ingredients.api';
import { uploadApi } from '../../api/upload.api';
import { getIngredientImageUrl } from '../../utils/images';
import { useAuth } from '../../context/AuthContext';
import { PhotoIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';

export default function IngredientsListPage() {
  const { isAdmin } = useAuth();
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRefs = useRef({});

  const fetchIngredients = async () => {
    setLoading(true);
    try {
      const response = await ingredientsApi.getAll();
      setIngredients(response.data.data);
    } catch (err) {
      console.error('Error fetching ingredients:', err);
      setError('Failed to load ingredients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIngredients();
  }, []);

  const handleImageUpload = async (ingredient, file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    const imageName = ingredient.ingredient_one
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .trim() + '.png';

    setUploadingId(ingredient.ingredient_one);
    setError('');
    setSuccess('');

    try {
      await uploadApi.uploadIngredientImage(file, imageName);
      setSuccess(`Image for "${ingredient.ingredient_one}" uploaded successfully`);
      // Force refresh images by updating state
      setIngredients(prev => prev.map(ing =>
        ing.ingredient_one === ingredient.ingredient_one
          ? { ...ing, image: imageName + '?t=' + Date.now() }
          : ing
      ));
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(`Failed to upload image for "${ingredient.ingredient_one}"`);
    } finally {
      setUploadingId(null);
    }
  };

  const filteredIngredients = ingredients.filter(ing => {
    const searchLower = search.toLowerCase();
    return (
      ing.ingredient_one?.toLowerCase().includes(searchLower) ||
      ing.ingredient_many?.toLowerCase().includes(searchLower) ||
      ing.ingredient_es_one?.toLowerCase().includes(searchLower) ||
      ing.ingredient_es_many?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Ingredients</h1>
        <p className="text-sm text-gray-500 mt-1">
          Ingredients from main and default recipes ({ingredients.length} total)
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg">
          {success}
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search ingredients..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Ingredients Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {filteredIngredients.map((ingredient) => {
          // Use cache-busted URL if image was just uploaded, otherwise use standard URL
          const baseImageUrl = getIngredientImageUrl(ingredient.ingredient_one);
          const imageUrl = ingredient.image?.includes('?t=')
            ? baseImageUrl + '?' + ingredient.image.split('?')[1]
            : baseImageUrl;
          const isUploading = uploadingId === ingredient.ingredient_one;

          return (
            <div
              key={ingredient.ingredient_one}
              className="bg-white rounded-lg shadow p-4 flex flex-col items-center"
            >
              {/* Image */}
              <div className="relative w-24 h-24 mb-3">
                <div className="w-full h-full rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center border">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={ingredient.ingredient_one}
                      className="h-full w-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <span className={`text-gray-400 text-xs ${imageUrl ? 'hidden' : 'flex'} flex-col items-center justify-center h-full w-full`}>
                    <PhotoIcon className="h-8 w-8 mb-1" />
                    No image
                  </span>
                </div>

                {/* Upload overlay for admins */}
                {isAdmin && (
                  <>
                    <input
                      ref={(el) => fileInputRefs.current[ingredient.ingredient_one] = el}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(ingredient, e.target.files[0])}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRefs.current[ingredient.ingredient_one]?.click()}
                      disabled={isUploading}
                      className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-50 transition-all flex items-center justify-center opacity-0 hover:opacity-100 rounded-lg"
                    >
                      {isUploading ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                      ) : (
                        <ArrowUpTrayIcon className="h-6 w-6 text-white" />
                      )}
                    </button>
                  </>
                )}
              </div>

              {/* Names */}
              <div className="text-center w-full">
                <p className="text-sm font-medium text-gray-900 truncate" title={ingredient.ingredient_one}>
                  {ingredient.ingredient_one}
                </p>
                <p className="text-xs text-gray-500 truncate" title={ingredient.ingredient_es_one}>
                  {ingredient.ingredient_es_one}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {filteredIngredients.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          {search ? 'No ingredients found matching your search.' : 'No ingredients found.'}
        </div>
      )}
    </DashboardLayout>
  );
}
